import React, { useState, useEffect, useCallback } from "react";
import Button from "@atlaskit/button/new";
import BugIcon from "@atlaskit/icon/core/bug";
import { useDispatch, useSelector } from "react-redux";
import { Popover, Collapse } from "antd";
import { runTestScripted, clearScriptResult } from "../../../../../../../../redux/actions/testFlowActions";
import debounce from "lodash/debounce";
import { isJsonResponse, prettyJson } from "../../../../../../../../common/utils/objects.util";
import { ActionType } from "../../../../../../../../common/interfaces/flow/action/ActionType";
import Tooltip from "@atlaskit/tooltip";
import AceEditor from "react-ace";
import "ace-builds/webpack-resolver";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-text";
import "ace-builds/src-noconflict/theme-dawn";
import { getAceEditorTheme } from "../../../../../../../../common/utils/aceEditorTheme";

const tryParseJson = (value) => {
  if (!value || !value.trim()) {
    return undefined;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const TestPaginationScriptButton = ({ action, scriptItem, appearance }) => {
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);

  const [scope, setScope] = useState("");
  const [responseData, setResponseData] = useState("");
  const [responseHeaders, setResponseHeaders] = useState("");
  const { Panel } = Collapse;
  const logs = useSelector((state) => state.integration.logs);
  const scriptResult = useSelector((state) => state.test.scriptResult);
  const actionType = action?.type ?? ActionType.SCRIPT_ACTION_TYPE;
  const scriptName = scriptItem?.name ?? "result";
  const canRunScript = Boolean(action && scriptItem && typeof scriptItem.script !== "undefined");

  useEffect(() => {
    if (logs && action?.id) {
      const actionLog = logs?.result?.find((log) => log.actionId === action.id) || null;
      if (actionLog) {
        setScope(prettyJson(actionLog));
      }
    }
  }, [logs, action?.id]);

  const buildScopePayload = useCallback(() => {
    const parsedScope = tryParseJson(scope);
    const baseScope =
      parsedScope && typeof parsedScope === "object" && !Array.isArray(parsedScope) ? parsedScope : parsedScope ? { scope: parsedScope } : {};

    const parsedResponseData = tryParseJson(responseData);
    const parsedResponseHeaders = tryParseJson(responseHeaders);

    const responsePayload = {
      ...(typeof parsedResponseData !== "undefined" && { data: parsedResponseData }),
      ...(typeof parsedResponseHeaders !== "undefined" && { headers: parsedResponseHeaders }),
    };

    return {
      ...baseScope,
      response: {
        ...(baseScope.response ?? {}),
        ...responsePayload,
      },
    };
  }, [scope, responseData, responseHeaders]);

  const doTest = useCallback(
    debounce(() => {
      if (!canRunScript) {
        return;
      }
      dispatch(runTestScripted(scriptItem, buildScopePayload(), actionType));
    }, 300),
    [dispatch, scriptItem, buildScopePayload, actionType, canRunScript]
  );

  const getSafeResult = (result) => {
    const evaluatedResult = isJsonResponse(result) ? prettyJson(result) : result;
    let resultText = evaluatedResult ? evaluatedResult : "result is undefined, no variable will be created";
    if (actionType === ActionType.SCRIPT_ACTION_TYPE) {
      resultText = evaluatedResult ? evaluatedResult : " result is empty";
    }

    return (
      <div className="scriptdebug">
        <AceEditor
          className="ihub-action-body-editor"
          mode="json"
          theme={getAceEditorTheme()}
          name="testPaginationScriptResult"
          editorProps={{ $blockScrolling: true }}
          value={resultText}
          readOnly
          width="100%"
          height="200px"
          setOptions={{ useWorker: false }}
        />
      </div>
    );
  };

  const renderContent = useCallback(
    () => (
      <>
        <div>
          <b>Debug pagination script</b>
        </div>
        <Collapse defaultActiveKey={[]} ghost>
          <Panel header="Scope" key="scope">
            <div className="scriptdebug">
              <AceEditor
                className="ihub-action-body-editor"
                mode="json"
                theme={getAceEditorTheme()}
                name="testPaginationScope"
                editorProps={{ $blockScrolling: true }}
                value={scope}
                onChange={(val) => setScope(val)}
                width="100%"
                height="200px"
                setOptions={{ useWorker: false }}
              />
              <span>Scope must be JSON. Ensure all keys are quoted properly and the syntax is correct.</span>
            </div>
          </Panel>
          <Panel header="Response data" key="responseData">
            <div className="scriptdebug">
              <AceEditor
                className="ihub-action-body-editor"
                mode="json"
                theme={getAceEditorTheme()}
                name="testPaginationResponseData"
                editorProps={{ $blockScrolling: true }}
                value={responseData}
                onChange={(val) => setResponseData(val)}
                width="100%"
                height="200px"
                setOptions={{ useWorker: false }}
              />
              <span>Provide the HTTP response body. JSON is recommended for easier parsing.</span>
            </div>
          </Panel>
          <Panel header="Response headers" key="responseHeaders">
            <div className="scriptdebug">
              <AceEditor
                className="ihub-action-body-editor"
                mode="json"
                theme={getAceEditorTheme()}
                name="testPaginationResponseHeaders"
                editorProps={{ $blockScrolling: true }}
                value={responseHeaders}
                onChange={(val) => setResponseHeaders(val)}
                width="100%"
                height="200px"
                setOptions={{ useWorker: false }}
              />
              <span>Include any headers your script depends on. JSON format works best.</span>
            </div>
          </Panel>
        </Collapse>
        <Button iconBefore={BugIcon} onClick={doTest} isDisabled={!canRunScript}>
          Run Script
        </Button>
        <p></p>
        {scriptResult && scriptResult.valid && (
          <>
            {actionType !== ActionType.SCRIPT_ACTION_TYPE && (
              <>
                <strong>Mustache: </strong>
                <code>&#123;&#123;{scriptName}&#125;&#125;</code>
              </>
            )}

            <br />
            <strong>Script result: </strong>
            {getSafeResult(scriptResult.value)}
          </>
        )}
        {scriptResult && !scriptResult.valid && (
          <>
            <strong>Script failed with result: </strong>
            <p></p>
            <code>{scriptResult.value}</code>
          </>
        )}
      </>
    ),
    [scope, responseData, responseHeaders, doTest, scriptResult, scriptName, actionType, canRunScript]
  );

  const togglePopUp = () => {
    if (isVisible) {
      dispatch(clearScriptResult());
      setIsVisible(false);
      return;
    }
    if (!canRunScript) {
      return;
    }
    setIsVisible(true);
  };

  const handleVisibilityChange = (open) => {
    if (!open) {
      dispatch(clearScriptResult());
      setIsVisible(false);
      return;
    }
    if (canRunScript) {
      setIsVisible(true);
    }
  };

  return (
    <div className="ihub-scripted-var-icons">
      <Popover content={renderContent()} title="" trigger="click" open={isVisible && canRunScript} onOpenChange={handleVisibilityChange}>
        <Tooltip position="top" content="Test pagination script">
          <Button
            iconBefore={BugIcon}
            onClick={togglePopUp}
            appearance={appearance ? appearance : "default"}
            isDisabled={!canRunScript}
          ></Button>
        </Tooltip>
      </Popover>
    </div>
  );
};

export default TestPaginationScriptButton;
